import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AppShell, type SeccionItem } from "@/components/shell/AppShell";
import { SimuladorSistema } from "@/components/credito/SimuladorSistema";
import { Comparativa } from "@/components/credito/Comparativa";
import { CalculadoraCat } from "@/components/credito/CalculadoraCat";
import { Landmark } from "lucide-react";

const TITULO = "Simulador de Amortización — Core de Crédito";
const DESC =
  "Simula tablas de amortización en sistemas Francés, Alemán y Americano y calcula el CAT conforme a Banxico, en pesos mexicanos.";

export const Route = createFileRoute("/amortizacion")({
  head: () => ({
    meta: [
      { title: TITULO },
      { name: "description", content: DESC },
      { property: "og:title", content: TITULO },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AmortizacionPage,
});

const SECCIONES: SeccionItem[] = [
  { id: "frances", label: "Francés" },
  { id: "aleman", label: "Alemán" },
  { id: "americano", label: "Americano" },
  { id: "comparativa", label: "Comparativa" },
  { id: "cat", label: "Calculadora CAT" },
];

function AmortizacionPage() {
  const [activa, setActiva] = useState<string>("frances");

  return (
    <AppShell
      titulo={TITULO}
      subtitulo="Demo funcional para SOFOM · Cálculo 100% en el navegador · Moneda MXN"
      icono={<Landmark className="h-5 w-5 text-primary-foreground" />}
      secciones={SECCIONES}
      activa={activa}
      onActiva={setActiva}
      pie="Herramienta de simulación con fines informativos y de comparación. El CAT se calcula conforme a la metodología de la Circular 21/2009 de Banco de México y no constituye una oferta de crédito."
    >
      {activa === "frances" && (
        <SimuladorSistema
          sistema="frances"
          titulo="Sistema Francés"
          descripcion="Cuota fija durante todo el plazo: el interés decrece y el capital amortizado crece período a período."
          formula="Cuota = M · i / (1 − (1 + i)^−n)"
          extraLabel="Cuota fija mensual"
        />
      )}
      {activa === "aleman" && (
        <SimuladorSistema
          sistema="aleman"
          titulo="Sistema Alemán"
          descripcion="Capital fijo por período y cuota decreciente: amortiza más rápido y genera menos intereses."
          formula="Capital = M / n · Cuota = M/n + saldo · i"
          extraLabel="Capital fijo mensual"
        />
      )}
      {activa === "americano" && (
        <SimuladorSistema
          sistema="americano"
          titulo="Sistema Americano (bullet)"
          descripcion="Solo se pagan intereses cada período y el capital se liquida completo en el último pago."
          formula="Pago = M · i · Último = M · i + M"
          extraLabel="Interés periódico"
        />
      )}
      {activa === "comparativa" && <Comparativa />}
      {activa === "cat" && <CalculadoraCat />}
    </AppShell>
  );
}
